import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import { makeStyles } from '@mui/styles';
import { Checkbox, FormControlLabel } from '@mui/material';

import Modal from '../../../common/Modal';
import { CancelOrderOptions } from './CancelOrderOptions';
import AutocompleteDropdown from '../../../common/autocompleteDropdown/AutocompleteDropdown';
import { serviceOrderStatuses } from '../../../../utils/constants';

const useStyles = makeStyles(() => ({
  formControl: {
    minWidth: 200,
  },
  select: {
    height: '50px',
    width: '205px',
  },
}));

export const EditOrderStatusModal = ({
  isOpen,
  onClose,
  currentValue,
  onSave,
  error,
  setError,
}) => {
  const materialClasses = useStyles();

  const [updatedStatus, setUpdatedStatus] = useState(null);
  const [overridePaymentValidation, setOverridePaymentValidation] = useState(false);
  const [cancelType, setCancelType] = useState('');
  const [cancelMessage, setCancelMessage] = useState(null);
  const isCancelling = updatedStatus === 'CANCELLED';

  const handleSaveStatus = () => {
    const savePayload = { updatedStatus, overridePaymentValidation };
    if (isCancelling) {
      savePayload.cancellationDetails = { type: cancelType, message: cancelMessage };
    }
    onSave(savePayload);
  };

  useEffect(() => {
    if (!isOpen) {
      setCancelMessage(null);
      setCancelType('');
      setUpdatedStatus(null);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal onClose={onClose} className="edit-order-status-modal-wrapper">
      <div className="edit-order-status-modal">
        <h1 className="edit-order-status-modal--header">Edit Status</h1>

        <section className="edit-order-status-modal--section">
          <div className="edit-order-status-modal--row">
            <label className="edit-order-status-modal--row-label">Current value</label>
            <p className="edit-order-status-modal--input">
              {currentValue === 'CANCELLED' ? 'CANCELED' : currentValue}
            </p>
          </div>
          <div className="edit-order-status-modal--row">
            <label className="edit-order-status-modal--row-label">Updated value</label>
            <div className="edit-order-status-modal--input">
              <FormControl variant="outlined" className={materialClasses.formControl}>
                <AutocompleteDropdown
                  selectedData={updatedStatus}
                  onChange={(event) => {
                    setError(null);
                    setCancelMessage(null);
                    setCancelType('');
                    setUpdatedStatus(event.value);
                    setOverridePaymentValidation(false);
                  }}
                  label="Status"
                  selectData={serviceOrderStatuses}
                />
              </FormControl>
            </div>
          </div>
          {updatedStatus === 'COMPLETED' && (
            <>
              <div className="edit-order-status-modal--row edit-order-status-modal--override-container">
                <FormControlLabel
                  control={
                    <Checkbox
                      value={overridePaymentValidation}
                      onChange={(event) => {
                        setOverridePaymentValidation(event.target.checked);
                      }}
                    />
                  }
                  label="Override Payment validation"
                />
              </div>
              {!!overridePaymentValidation && (
                <p className="edit-order-status-modal--row edit-order-status-modal--disclaimer">
                  By overriding the payment validation, you will be marking the order as complete,
                  even if the order has not been fully paid. This may result in reporting
                  discrepancies for this order.
                </p>
              )}
            </>
          )}
        </section>

        {isCancelling && (
          <CancelOrderOptions
            setType={setCancelType}
            setMessage={setCancelMessage}
            type={cancelType}
            message={cancelMessage}
          />
        )}

        <section className="edit-order-status-modal--section">
          {error && <p className="error-text">{error}</p>}
          <button
            className="edit-order-status-modal--submit-button"
            onClick={handleSaveStatus}
            disabled={isCancelling && !cancelType}
          >
            Save Changes
          </button>
        </section>
      </div>
    </Modal>
  );
};

EditOrderStatusModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  currentValue: PropTypes.string,
  onSave: PropTypes.func,
  error: PropTypes.string,
  setError: PropTypes.func,
};
